import Button from "@/common/components/Button";
import MaskedTextField from "@/common/components/MaskedTextField";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import { numericRegexMask } from "@/common/constants/masks.constant";
import useDebounce from "@/common/hooks/useDebounce";
import { Mapper } from "@/common/services/mapper";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { ProductData } from "@/common/types/product.type";
import { FormTransformers } from "@/common/utils/formTransformers";
import { tefProviders } from "@/features/establishments/constants/tefModels.constant";
import { useCreateTefProductCode } from "@/features/establishments/hooks/tefConfiguration/useCreateTefProductCode";
import { useEstablishmentAllowedProducts } from "@/features/establishments/hooks/useEstablishmentAllowedProducts";
import { tefProductCodeCreateSchema, TefProductCodeCreateValidationSchema } from "@/features/establishments/schemas/tefConfigurations/tefProductCode.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

interface TefConfigurationProductCreateModalProps {
  provider?: string;
  establishment?: EstablishmentCompanyData;
}

const TefConfigurationProductCreateModal = ({ provider, establishment }: TefConfigurationProductCreateModalProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [searchProducts, setSearchProducts] = useDebounce('');

  const {
    mutate: createTefProductCode,
    isPending: createTefProductCodeIsPending,
    isError: createTefProductCodeIsError,
  } = useCreateTefProductCode({
    provider,
    establishmentId: establishment?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tefProductCodes'] });
      setMessages(['Produto adicionado com sucesso!']);
      setIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  })

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors }
  } = useForm<TefProductCodeCreateValidationSchema>({
    resolver: zodResolver(tefProductCodeCreateSchema),
    defaultValues: {
      product_id: undefined,
      code: '',
    },
  });

  const onSubmit: SubmitHandler<TefProductCodeCreateValidationSchema> = (data) => createTefProductCode(data);

  useEffect(reset, [isOpen]);

  const { data: products } = useEstablishmentAllowedProducts({
    enabled: !!establishment?.id,
    filters: {
      search: searchProducts,
      page: 1,
    establishmentId: establishment?.id,
    },
    mapper: Mapper.mapToOptions<ProductData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  });

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            name="create_tef_product_code"
            operationKlassToCheckPermission="Admin::Establishment::TefProductCodes::Create"
          >
            Adicionar novo produto
          </Button>
        )}
        dataTestid="submit_create_tef_product_code"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={createTefProductCodeIsPending}
        disabled={createTefProductCodeIsPending}
        footer="Confirmar adição e salvar alteração"
        title="Adicionar novo produto"
        body={(
          <div className="flex flex-col gap-y-4 text-white-800">
            <SelectController
              control={control}
              name="product_id"
              label="Produto"
              options={products}
              onInputChange={setSearchProducts}
              markAsRequired
              errorMessage={errors.product_id?.message || undefined}
            />

            <Controller
              control={control}
              name="code"
              render={({ field }) => (
                <MaskedTextField
                  {...field}
                  label={`Código ${provider ? tefProviders[provider]?.label : ''}`}
                  mask={provider ? tefProviders[provider].regexMaskFn(tefProviders[provider].codeLength) : numericRegexMask(6)}
                  prepareChar={(char) => char.toUpperCase()}
                  markAsRequired
                  errorMessage={errors.code?.message || undefined}
                />
              )}
            />
          </div>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={createTefProductCodeIsError}
        />
      )}
    </>
  )
}

export default TefConfigurationProductCreateModal;
